import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './style.module.scss';
import TabsBolao from '../../Components/TabsBolao';
import CardBoloes from '../../Components/CardBoloes';
import { useCart } from '../../contexts/CartProvider';
import { api } from '../../services/apiClient';
import { toast } from 'react-toastify';
import VerApostasBolao from '../../Components/VerApostasBolao';
import { useAuth } from '../../contexts/AuthContext';

const Bolao = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { user, logout, isAuthenticated } = useAuth();

  // States
  const [subBoloesSelect, setSubBoloesSelect] = useState(null);
  const [bolaoName, setBolaoName] = useState('Bolão');
  const [apostasBolao, setApostasBolao] = useState([]);
  const [apostaId, setApostaId] = useState(null);
  const [apostas, setApostas] = useState([]);
  const [apostasBuscarBolao, setApostasBuscarBolao] = useState([]);
  const [modalVerApostas, setModalVerApostas] = useState(false);
  const [concursoSelecionado, setConcursoSelecionado] = useState(null);
  const [arrayDeBoloes, setArrayDeBoloes] = useState([]);
  const [boloes, setBoloes] = useState([]);
  const [activeKey, setActiveKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [subBolaoData, setSubBolaoData] = useState({
    regra: null,
    cotas: null,
    subBolaoCart: null,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [dataRanking, setDataRankin] = useState([]);
  const [userRanking, setUserRanking] = useState(null);

  const tabsBolaoRef = useRef(null);
  const bolaoconcurso = 4127;

  // Modal handler
  const modalSetConcursoAndState = async (concurso) => {
    setModalVerApostas(true);
    try {
      const apostas = await api.get(`/boloes_get/${concurso.jogo_id}`);
      const resultados = apostas.data.boloes;
      const filteredBoloesArray = resultados.filter((b) => b.codigo === concurso.codigo);
      setApostasBuscarBolao(filteredBoloesArray);
    } catch (error) {
      console.error('Erro ao buscar dados', error);
    }
    setConcursoSelecionado(concurso);
  };

  // Fetch boloes data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [jogosRes, jogosEspecialRes] = await Promise.all([api.get('/jogos_get'), api.get('/jogos_especial_get')]);

        const jogosEspecial = jogosEspecialRes.data.map((bolao) => ({ ...bolao, especial: 1 }));
        const combinedBoloes = [...jogosRes.data, ...jogosEspecial];

        // Create map to ensure uniqueness and prioritize special boloes
        const bolaoMap = new Map();
        combinedBoloes.forEach((bolao) => {
          if (!bolaoMap.has(bolao.id) || bolao.especial) {
            bolaoMap.set(bolao.id, bolao);
          }
        });

        const uniqueBoloes = Array.from(bolaoMap.values());
        setBoloes(uniqueBoloes);
      } catch (error) {
        console.error('Erro ao buscar dados', error);
        toast.error('Erro ao carregar bolões');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Set active bolao based on URL param
  useEffect(() => {
    if (id && boloes.length > 0) {
      const bolao = boloes.find((b) => b.id.toString() === id);
      if (bolao) {
        setBolaoName(bolao.nome);
        setSubBoloesSelect(bolao);
        setActiveKey(bolao.id.toString());
      }
    } else if (boloes.length > 0) {
      setSubBoloesSelect(boloes[0]);
      setActiveKey(boloes[0].id.toString());
    }
  }, [id, boloes]);

  // Scroll to tabs when active key changes
  useEffect(() => {
    if (tabsBolaoRef.current) {
      tabsBolaoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeKey]);

  // Scroll to top when loading
  useEffect(() => {
    if (loading) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [loading]);

  // Add to cart handler
  const handleAddToCart = (nome, subbolao, quantidade, dataSorteio, nomeBolao) => {
    addToCart({
      id: subbolao.id,
      grupo: subbolao.grupo,
      numero_cotas: quantidade,
      quantidade,
      valor: subbolao.valor_cota,
      nomeUsuario: nome,
      addedAt: new Date(),
      nome: nomeBolao,
      status: subbolao.status,
      data_sorteio: dataSorteio,
    });

    toast.success(`Bolão adicionado com sucesso!!`, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'light',
    });
  };

  // Fetch apostas data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const apostas = await api.get('/boloes_get');
        setApostasBolao(apostas.data);
      } catch (error) {
        console.error('Erro ao buscar dados', error);
      }
    };
    fetchData();
  }, []);

  // Find and set bolao based on ID
  useEffect(() => {
    const findAndSetBolao = () => {
      if (apostasBolao && apostasBolao.length > 0) {
        const selectedBolao = apostasBolao.find((aposta) => aposta.jogo_id === Number(id));
        if (selectedBolao) {
          setApostas(selectedBolao.apostas);
          setArrayDeBoloes(selectedBolao);
        } else {
          console.log('Bolão não encontrado com o ID:', id);
        }
      }
    };
    findAndSetBolao();
  }, [id, apostasBolao]);

  // Close modal handler
  const closeModal = () => {
    setModalVerApostas(false);
  };

  // Show modal handler
  const showModal = useCallback((subbolao) => {
    setIsLoading(true);
    setSubBolaoData({
      regra: subbolao.regra,
      cotas: subbolao.cotas,
      subBolaoCart: subbolao,
    });
    setIsModalVisible(true);

    if (subbolao && subbolao.id) {
      setApostaId(subbolao.id);
      const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?bolaoId=${subbolao.id}`;
      window.history.pushState({ path: newurl }, '', newurl);
    }
    setIsLoading(false);
  }, []);

  // Fetch ranking data
  useEffect(() => {
    const fetchRanking = async () => {
      try {
        const response = await api.get('/jogos_especial_get');
        const apostas = await api.get(`/boloes_get/${bolaoconcurso}`);
        setDataRankin(response.data);
        setUserRanking(apostas.data);
      } catch (error) {
        console.error('Erro ao buscar dados da API:', error);
      }
    };
    fetchRanking();
  }, [bolaoconcurso]);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className={styles.loadingContainer}>
      <div className={styles.skeletonTabs}></div>
      <div className={styles.skeletonGrid}>
        {[...Array(4)].map((_, index) => (
          <div key={index} className={styles.skeletonCard}>
            <div className={styles.skeletonHeader}></div>
            <div className={styles.skeletonContent}>
              <div className={styles.skeletonLine}></div>
              <div className={styles.skeletonLine}></div>
              <div className={styles.skeletonLine}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{bolaoName} - Tribo da sorte</title>
      </Helmet>

      {/* Page Header */}
      <div className={styles.pageHeader}>
        <h1>Escolha entre os grupos de bolões</h1>
        <p>Encontre o bolão perfeito para você e aumente suas chances de ganhar!</p>
      </div>

      {/* Loading State */}
      {loading && <LoadingSkeleton />}

      {/* Tabs Section */}
      {!loading && (
        <div ref={tabsBolaoRef} className={styles.tabsSection}>
          <TabsBolao boloes={boloes} setSubBoloesSelect={setSubBoloesSelect} activeKey={activeKey} setActiveKey={setActiveKey} />
        </div>
      )}

      {/* Modals */}
      {modalVerApostas && <VerApostasBolao color={subBoloesSelect?.cor || '#feac24'} bolaoName={bolaoName} apostas={apostasBuscarBolao} isModalVisible={modalVerApostas} closeModal={closeModal} />}

      {/* Special Ranking Section for Lotofácil Independência */}
      {id === '4127' && (
        <div className={styles.rankingSection}>
          <div className={styles.rankingHeader}>
            <h2>
              Olha só como você está no Ranking de maiores compradores da <strong>LOTOFÁCIL INDEPENDÊNCIA</strong>
            </h2>
            <h3>
              O primeiro colocado vai levar um prêmio de <strong>R$ 1.000,00 no PIX</strong> e mais <strong>R$ 1.000,00 de SALDO</strong>
            </h3>
          </div>

          <div className={styles.rankingGrid}>
            {/* User Position */}
            <div className={`${styles.rankingCard} ${styles.userCard}`}>
              <div className={styles.rankingAvatar}>
                <span>Você</span>
              </div>
              <div className={styles.rankingPosition}>#{userRanking?.posicaoUsuarioLogado?.ranking || 0}</div>
              <div className={styles.rankingInfo}>
                <h4>Você</h4>
                <p>{userRanking?.posicaoUsuarioLogado?.total_cotas || 0} cotas</p>
              </div>
            </div>

            {/* Top Players */}
            {dataRanking[0]?.dadosAdicionais?.map((item, index) => (
              <div key={index} className={styles.rankingCard}>
                <div className={styles.rankingAvatar}>
                  <span>{item.nome.substring(0, 2).toUpperCase()}</span>
                </div>
                <div className={styles.rankingPosition}>#{item.ordem}</div>
                <div className={styles.rankingInfo}>
                  <h4>{item.nome}</h4>
                  <p>{item.soma_cotas} cotas</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cards Section */}
      {!loading && subBoloesSelect && (
        <CardBoloes
          modalSetConcursoAndState={modalSetConcursoAndState}
          quantidadeApostas={apostas?.length}
          setApostaId={setApostaId}
          boloesArray={boloes}
          setModalVerApostas={setModalVerApostas}
          bolao={subBoloesSelect}
          handleAddToCart={handleAddToCart}
          showModal={showModal}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          subBolaoData={subBolaoData}
          setSubBolaoData={setSubBolaoData}
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
        />
      )}
    </div>
  );
};

export default Bolao;
